import UserAvatar from 'components/userAvatar';
import { useMemo, useState } from 'react';
import { Input, Select, Pagination, Table, Tag, Switch } from 'antd';
import { SearchOutlined } from '@ant-design/icons';

const formatEpoch = (epoch) => {
  if (!epoch) return '-';

  // If epoch is in seconds
  const date = new Date(epoch * 1000);

  return date.toLocaleString();
};

// Handles is_active coming through as boolean, number, or string ('1'/'0')
const isActiveTruthy = (value) =>
  value === true || value === 1 || value === '1';

export default function DataTable({
  columns = [],
  data = [],
  onToggleActive,
  togglingRowKey,
}) {
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const filteredData = useMemo(() => {
    if (!search) return data;

    return data.filter((row) =>
      Object.values(row)
        .map((v) => (v ?? '').toString())
        .join(' ')
        .toLowerCase()
        .includes(search.toLowerCase()),
    );
  }, [search, data]);

  const paginatedData = filteredData.slice(
    (page - 1) * rowsPerPage,
    page * rowsPerPage,
  );

  const antColumns = columns.map((col) => ({
    title: col.label,
    dataIndex: col.accessor,
    key: col.accessor,

    sorter: (a, b) =>
      (a[col.accessor] ?? '')
        .toString()
        .localeCompare((b[col.accessor] ?? '').toString()),

    render: (text, row) => {
      /* Username — avatar + email */
      if (col.accessor === 'username') {
        const username = row.username ?? ' ';
        const email = row.email ?? '';

        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
           <UserAvatar username={username} email={email} size={30} />
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <span>{username}</span>
              {email && (
                <span style={{ fontSize: 12, color: '#888' }}>
                  {email}
                </span>
              )}
            </div>
          </div>
        );
      }

      /* Last Seen — epoch → datetime */
      if (col.accessor === 'last_seen') {
        return <span>{formatEpoch(text)}</span>;
      }

      /* Invite Status — Tag */
      if (col.accessor === 'invite_status') {
        const status = text?.toUpperCase();

        return (
          <Tag
            color={
              status === 'ACTIVE'
                ? 'green'
                : status === 'PENDING'
                ? 'gold'
                : 'blue'
            }
          >
            {status ?? '-'}
          </Tag>
        );
      }

      /* Active Status — Switch to activate/deactivate the user */
      if (col.accessor === 'is_active') {
        const rowKey = row.id ?? row.user_id ?? row.username;

        return (
          <Switch
            checked={isActiveTruthy(text)}
            loading={togglingRowKey === rowKey}
            onChange={(checked) => onToggleActive?.(row, checked)}
          />
        );
      }

      /* Default */
      return <span>{text ?? '-'}</span>;
    },
  }));

  return (
    <div>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          marginBottom: 20,
        }}
      >
        <Input
          placeholder="Search by name or email"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(1);
          }}
          allowClear
          prefix={<SearchOutlined style={{ color: '#bbb' }} />}
          style={{ width: 260 }}
        />

        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 14 }}>Results per page:</span>
          <Select
            value={rowsPerPage}
            onChange={(value) => {
              setRowsPerPage(value);
              setPage(1);
            }}
            style={{ width: 90 }}
            options={[5, 10, 20, 50].map((n) => ({
              label: n,
              value: n,
            }))}
          />
        </div>
      </div>

      <Table
        columns={antColumns}
        sticky={false}
        dataSource={paginatedData}
        pagination={false}
        rowKey={(record, index) => index}
        rowClassName={() => 'no-hover'}
        className="no-column-lines"
        style={{ border: 'none' }}
      />

      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          marginTop: 20,
        }}
      >
        <Pagination
          current={page}
          total={filteredData.length}
          pageSize={rowsPerPage}
          onChange={setPage}
          showSizeChanger={false}
        />
      </div>
    </div>
  );
}
